import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Box,
    Tab,
    Tabs,
    CircularProgress,
    InputAdornment,
    Autocomplete,
    Chip,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { GroupsTeamsService } from '../../../../services/groups-teams.service';
import { TeamService } from '../../../../services/team.service';
import { TagsService } from '../../../../services/tags-service';
import { toast } from 'react-toastify';
import { api } from '../../../../http';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { useEffectiveSettings, SETTING } from '../../../../hooks/use-effective-settings';

interface AddMemberModalProps {
    open: boolean;
    onClose: () => void;
    teamId: string;
    teamName: string;
    onAdded?: () => void;
}

interface PomUser {
    id: string;
    firstName: string;
    lastName: string;
    phone?: string;
    phoneNumber?: string;
    employeeId?: string;
    teamId?: string;
    teamName?: string;
}

interface TagOption {
    id: string;
    name: string;
    isNew?: boolean;
}

const tagFilter = createFilterOptions<TagOption>();

export const AddMemberModal: React.FC<AddMemberModalProps> = ({
    open,
    onClose,
    teamId,
    teamName,
    onAdded,
}) => {
    const { getSetting } = useEffectiveSettings();
    const uniqueId = getSetting<string>(SETTING.userUniqueIdentifier, '');

    const [tab, setTab] = useState(0);

    // Find existing
    const [filterText, setFilterText] = useState('');
    const [allUsers, setAllUsers] = useState<PomUser[]>([]);
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState<string | null>(null);
    const [addedIds, setAddedIds] = useState<Set<string>>(new Set());

    // Create new
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [email, setEmail] = useState('');
    const [selectedTags, setSelectedTags] = useState<TagOption[]>([]);
    const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
    const [creating, setCreating] = useState(false);

    // Determine required fields based on org setting
    const isPhoneRequired = !uniqueId || uniqueId === 'INVITECODE' || uniqueId === 'PHONENUMBER';
    const isEmployeeIdRequired = uniqueId === 'EMPLOYEEID';
    const isEmailRequired = uniqueId === 'EMAILADDRESS';

    // Get teams from Redux store for resolving team names
    const teams = useSelector((state: RootState) => (state as any).teams?.items || []);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const PAGE_SIZE = 50;

    // Reactive teamId -> team lookup, rebuilt whenever the teams store changes.
    // The team name MUST be resolved at render time (below), not baked into the
    // mapped user at fetch time: TeamService.fetchAll() is async, so the first
    // fetchUsers() ran against an empty store and stamped every candidate as
    // "Unknown Team", and the mapped list was never recomputed when teams loaded.
    // That made every user show "Unknown" even with a valid team (#support 2026-07-15).
    const teamsById = useMemo(() => {
        const m = new Map<string, any>();
        (teams as any[]).forEach((t) => { if (t && t.id != null) m.set(String(t.id), t); });
        return m;
    }, [teams]);

    const mapUser = (p: any) => ({
        id: p.id || p._id,
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        phone: p.phoneNumber || p.phone,
        employeeId: p.employeeId,
        teamId: p.teamId,
    });

    const fetchUsers = (query: string, skip = 0, append = false) => {
        if (skip === 0) setLoading(true); else setLoadingMore(true);
        api.get('users/getSubscriptionUsers', { params: { search: query || undefined, limit: PAGE_SIZE, skip } })
            .then(({ data }) => {
                if (data.isValid && Array.isArray(data.result)) {
                    const mapped = data.result.map(mapUser);
                    setAllUsers((prev) => append ? [...prev, ...mapped] : mapped);
                    setHasMore(data.result.length === PAGE_SIZE);
                }
            })
            .catch(() => { if (skip === 0) toast.error('Failed to load users.'); })
            .finally(() => { setLoading(false); setLoadingMore(false); });
    };

    const handleScroll = (e: React.UIEvent<HTMLElement>) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 100 && hasMore && !loadingMore && !loading) {
            fetchUsers(filterText.trim(), allUsers.length, true);
        }
    };

    useEffect(() => {
        if (open) {
            // Always refresh so teams created during this session (e.g. mid-training)
            // resolve; the render-time lookup below picks up the store update.
            TeamService.fetchAll();
            setHasMore(true);
            fetchUsers('');
            TagsService.fetchAll().then((tags) => {
                if (tags) setAvailableTags(tags.map((t: any) => ({ id: t._id || t.id, name: t.name })));
            }).catch((err) => console.error('[AddMemberModal] tag fetch failed', err));
        }
    }, [open]);

    // Debounced API search when filter text changes
    useEffect(() => {
        if (!open) return;
        const timer = setTimeout(() => { setHasMore(true); fetchUsers(filterText.trim()); }, 300);
        return () => clearTimeout(timer);
    }, [filterText, open]);

    const filtered = allUsers;

    const handleClose = () => {
        setFilterText('');
        setAllUsers([]);
        setAddedIds(new Set());
        setFirstName('');
        setLastName('');
        setPhone('');
        setEmployeeId('');
        setEmail('');
        setSelectedTags([]);
        setTab(0);
        onClose();
    };

    const handleAddExisting = (userId: string) => {
        setAdding(userId);
        const user = allUsers.find((u) => u.id === userId);
        GroupsTeamsService.addMember(teamId, userId, (isValid, message) => {
            setAdding(null);
            if (isValid) {
                toast.success('Member added.');
                setAddedIds((prev) => new Set(prev).add(userId));
                onAdded?.();
            } else {
                toast.error(message || 'Failed to add member.');
            }
        }, user ? { firstName: user.firstName, lastName: user.lastName, phoneNumber: user.phone, employeeId: user.employeeId } : undefined);
    };

    const isCreateDisabled = () => {
        if (!firstName.trim() || !lastName.trim()) return true;
        if (isPhoneRequired && !phone.trim()) return true;
        if (isEmployeeIdRequired && !employeeId.trim()) return true;
        if (isEmailRequired && !email.trim()) return true;
        return creating;
    };

    const handleCreateAndAdd = async () => {
        if (isCreateDisabled()) return;
        setCreating(true);
        try {
            const { data } = await api.post('users/addUser', {
                payload: {
                    firstName: firstName.trim(),
                    lastName: lastName.trim(),
                    phoneNumber: phone.trim() ? `+1${phone.trim()}` : undefined,
                    employeeId: employeeId.trim() || undefined,
                    backupEmail: email.trim() || undefined,
                    tags: selectedTags.map((t) => t.id),
                    teamId,
                },
                uniqueId: uniqueId || 'INVITECODE',
            });
            setCreating(false);
            if (data.isValid) {
                toast.success('User created and added to team.');
                setFirstName('');
                setLastName('');
                setPhone('');
                setEmployeeId('');
                setEmail('');
                setSelectedTags([]);
                onAdded?.();
            } else {
                toast.error(data.message || 'Failed to create user.');
            }
        } catch (err: any) {
            setCreating(false);
            const msg = err?.response?.data?.message;
            toast.error(msg || 'Failed to create user.');
        }
    };

    const RequiredStar = () => <span style={{ color: '#d32f2f' }}>*</span>;
    const OptionalHint = () => (
        <span style={{ fontWeight: 400, fontStyle: 'italic', textTransform: 'none' as const, letterSpacing: 0 }}>
            (optional)
        </span>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Add Member to {teamName}</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: GT_COLORS.textFaint }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{ px: 3, '& .MuiTab-root': { textTransform: 'none', fontSize: '14px', minHeight: 40 } }}
            >
                <Tab label="Find Existing User" />
                <Tab label="Create New User" />
            </Tabs>

            <DialogContent sx={{ pt: 2 }}>
                {tab === 0 && (
                    <Box>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Filter by name, phone, or employee ID..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon sx={{ mr: 1, color: GT_COLORS.textFaint, fontSize: '18px' }} />
                                ),
                            }}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                        />

                        {loading && (
                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {!loading && filtered.length === 0 && (
                            <Typography
                                sx={{ fontSize: '14px', color: GT_COLORS.textFaint, textAlign: 'center', py: 3 }}
                            >
                                {filterText ? 'No matching users.' : 'No POM users found.'}
                            </Typography>
                        )}

                        <Box sx={{ minHeight: 280, maxHeight: 320, overflow: 'auto' }} onScroll={handleScroll}>
                            {filtered.map((user) => {
                                const justAdded = addedIds.has(user.id);
                                const alreadyOnTeam = user.teamId === teamId;
                                const isMember = justAdded || alreadyOnTeam;
                                // Resolve the current team name reactively from the store.
                                const currentTeam = user.teamId ? teamsById.get(String(user.teamId)) : null;

                                return (
                                    <Box
                                        key={user.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: '8px',
                                            px: '10px',
                                            borderRadius: '6px',
                                            opacity: isMember ? 0.6 : 1,
                                            '&:hover': { backgroundColor: GT_COLORS.hoverBg },
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                {user.phone && (
                                                    <Typography
                                                        sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}
                                                    >
                                                        {user.phone}
                                                    </Typography>
                                                )}
                                                {isMember ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            padding: '1px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '11px',
                                                            fontWeight: 500,
                                                            backgroundColor: GT_COLORS.greenLight,
                                                            color: GT_COLORS.green,
                                                        }}
                                                    >
                                                        Member
                                                    </Box>
                                                ) : user.teamId ? (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            padding: '1px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '11px',
                                                            fontWeight: 500,
                                                            backgroundColor: GT_COLORS.amberLight,
                                                            color: GT_COLORS.amberDark,
                                                        }}
                                                    >
                                                        Currently on: {currentTeam?.name || 'Unknown Team'}
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            display: 'inline-flex',
                                                            padding: '1px 6px',
                                                            borderRadius: '3px',
                                                            fontSize: '11px',
                                                            fontWeight: 500,
                                                            backgroundColor: GT_COLORS.badgeGrayBg,
                                                            color: GT_COLORS.badgeGrayColor,
                                                        }}
                                                    >
                                                        Unassigned
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                        {isMember ? (
                                            <Typography
                                                sx={{
                                                    fontSize: '12px',
                                                    color: GT_COLORS.textFaint,
                                                    fontStyle: 'italic',
                                                }}
                                            >
                                                {justAdded ? 'Just added' : 'Already on team'}
                                            </Typography>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleAddExisting(user.id)}
                                                disabled={adding === user.id}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontSize: '13px',
                                                    borderRadius: '6px',
                                                    color: GT_COLORS.primary,
                                                    borderColor: GT_COLORS.primary,
                                                    minWidth: 60,
                                                }}
                                            >
                                                {adding === user.id ? (
                                                    <CircularProgress size={14} />
                                                ) : (
                                                    'Add'
                                                )}
                                            </Button>
                                        )}
                                    </Box>
                                );
                            })}
                            {loadingMore && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {tab === 1 && (
                    <Box>
                        <FieldLabel>
                            First Name <RequiredStar />
                        </FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            autoFocus
                            sx={{
                                mb: '14px',
                                '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' },
                            }}
                        />
                        <FieldLabel>
                            Last Name <RequiredStar />
                        </FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            sx={{
                                mb: '14px',
                                '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' },
                            }}
                        />
                        <FieldLabel>
                            Phone Number {isPhoneRequired ? <RequiredStar /> : <OptionalHint />}
                        </FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={phone}
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                                setPhone(digits);
                            }}
                            placeholder="5551234567"
                            inputProps={{ maxLength: 10 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: GT_COLORS.textMuted }}>
                                            +1
                                        </Typography>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: '14px',
                                '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' },
                            }}
                        />
                        <FieldLabel>
                            Employee ID {isEmployeeIdRequired ? <RequiredStar /> : <OptionalHint />}
                        </FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={employeeId}
                            onChange={(e) => setEmployeeId(e.target.value)}
                            sx={{
                                mb: '14px',
                                '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' },
                            }}
                        />
                        <FieldLabel>
                            Email {isEmailRequired ? <RequiredStar /> : <OptionalHint />}
                        </FieldLabel>
                        <TextField
                            fullWidth
                            size="small"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="user@example.com"
                            sx={{
                                mb: '14px',
                                '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' },
                            }}
                        />
                        <FieldLabel>
                            Tags <OptionalHint />
                        </FieldLabel>
                        <Autocomplete
                            multiple
                            freeSolo
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            size="small"
                            options={availableTags}
                            getOptionLabel={(o) => typeof o === 'string' ? o : o.name}
                            value={selectedTags}
                            onChange={async (_, val) => {
                                const last = val[val.length - 1];
                                if (last && typeof last !== 'string' && last.isNew) {
                                    const created = await TagsService.createTag({ name: last.name });
                                    if (created) {
                                        const newTag = { id: (created as any)._id || (created as any).id, name: (created as any).name };
                                        setAvailableTags((prev) => [...prev, newTag]);
                                        setSelectedTags([
                                            ...val.slice(0, -1) as TagOption[],
                                            newTag,
                                        ]);
                                    } else {
                                        toast.error('Failed to create tag.');
                                    }
                                } else {
                                    setSelectedTags(val as TagOption[]);
                                }
                            }}
                            filterOptions={(options, params) => {
                                const filtered = tagFilter(options, params);
                                const { inputValue } = params;
                                const exists = options.some(
                                    (o) => o.name.toLowerCase() === inputValue.toLowerCase()
                                );
                                if (inputValue !== '' && !exists) {
                                    filtered.push({
                                        id: `new-${inputValue}`,
                                        name: inputValue,
                                        isNew: true,
                                    });
                                }
                                return filtered;
                            }}
                            renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                    {option.isNew ? (
                                        <Typography sx={{ fontSize: '14px' }}>
                                            Create &quot;{option.name}&quot;
                                        </Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
                                    )}
                                </li>
                            )}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            renderTags={(value, getTagProps) =>
                                value.map((tag, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={tag.id}
                                        label={tag.name}
                                        size="small"
                                        sx={{
                                            fontSize: '12px',
                                            height: '22px',
                                            backgroundColor: GT_COLORS.badgeTagBg,
                                            color: GT_COLORS.badgeTagColor,
                                        }}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={selectedTags.length === 0 ? 'Select or create tags...' : ''}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '14px', borderRadius: '8px' } }}
                                />
                            )}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                {tab === 0 && (
                    <Button
                        onClick={handleClose}
                        variant="contained"
                        sx={{
                            backgroundColor: GT_COLORS.primary,
                            textTransform: 'none',
                            fontSize: '14px',
                            borderRadius: '8px',
                            '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                        }}
                    >
                        Done
                    </Button>
                )}
                {tab === 1 && (
                    <>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: GT_COLORS.textMuted,
                                borderColor: GT_COLORS.borderInput,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                            }}
                        >
                            Done
                        </Button>
                        <Button
                            onClick={handleCreateAndAdd}
                            disabled={isCreateDisabled()}
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            sx={{
                                backgroundColor: GT_COLORS.primary,
                                textTransform: 'none',
                                fontSize: '14px',
                                borderRadius: '8px',
                                '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                            }}
                        >
                            {creating ? 'Creating...' : 'Create & Add to Team'}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography
        sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: GT_COLORS.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '.5px',
            mb: '4px',
        }}
    >
        {children}
    </Typography>
);
